import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { InlineEdit } from "@/components/InlineEdit";
import { BadgeMultiSelect } from "@/components/BadgeMultiSelect";
import { RecordPanel } from "@/components/record/RecordFrame";
import { invalidateCompanyQueries } from "@/lib/companyQueries";
import { formatDealValue } from "@shared/formatDealValue";
import { BUYER_FIRM_TYPES } from "@shared/buyerFirmTypes";
import { BUYER_THEMES } from "@shared/buyerOptions";
import type { CompanyData } from "../companyTypes";

const FIRM_TYPES = [...BUYER_FIRM_TYPES];
const INDUSTRY_OPTIONS = ["Healthcare", "Technology", "Manufacturing", "Business Services", "Consumer", "Energy", "Industrial", "Financial Services", "SaaS & Technology", "Transportation & Logistics", "Construction & Infrastructure", "Food & Beverage", "Other"];
const THEME_OPTIONS = [...BUYER_THEMES];
const GEO_OPTIONS = ["Texas", "Oklahoma", "Colorado", "Kansas", "Arkansas", "Louisiana", "New Mexico", "Arizona", "California", "Florida", "Georgia", "Illinois", "New York", "Ohio", "Pennsylvania", "Tennessee", "North Carolina", "Virginia", "Washington", "National"];

const Label = ({ children }: { children: ReactNode }) => (
  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</div>
);

/** What the buyer looks for. Every field edits in place; an unset one says Add. */
export function BuyerCriteriaPanel({ company, saveCompanyFieldAsync }: {
  company: CompanyData; saveCompanyFieldAsync: (field: string, value: unknown) => Promise<unknown>;
}) {
  const qc = useQueryClient();
  const c = company.acquisitionCriteria;

  // PUT /criteria replaces the row, so send the stored row with the change on
  // top, in one request. Two back-to-back PUTs (the old sector save) let the
  // second overwrite the first with stale values.
  const save = async (updates: Record<string, unknown>) => {
    const body: Record<string, unknown> = { ...(c ?? {}), ...updates };
    delete body.id; delete body.companyId; delete body.createdAt; delete body.updatedAt;
    const r = await fetch(`/api/companies/${company.id}/criteria`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Couldn't save the criteria. Try again.");
    invalidateCompanyQueries(qc, company.id);
    toast.success("Saved");
  };
  // BadgeMultiSelect does not await its save, so report a failure here.
  const saveList = (updates: Record<string, unknown>) => { save(updates).catch((e: Error) => toast.error(e.message)); };

  return (
    <RecordPanel title="Investment criteria">
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <Label>Firm type</Label>
          <InlineEdit label="Firm type" value={company.subType} type="select" options={FIRM_TYPES} addLabel="Add" onSave={(v) => saveCompanyFieldAsync("subType", v || null)} />
        </div>
        <div>
          <Label>Platform deal size</Label>
          <div className="flex items-center gap-1.5">
            <InlineEdit label="Minimum deal size" value={c?.minDealSize} placeholder="e.g. $10M" addLabel="Add" onSave={(v) => save({ minDealSize: formatDealValue(v) })} />
            <span className="text-muted-foreground">to</span>
            <InlineEdit label="Maximum deal size" value={c?.maxDealSize} placeholder="e.g. $50M" addLabel="Add" onSave={(v) => save({ maxDealSize: formatDealValue(v) })} />
          </div>
        </div>
        <div>
          <Label>Geographic focus</Label>
          <InlineEdit label="Geographic focus" value={c?.geoFocus === "focused" ? "Focused" : "National"} type="select" options={["National", "Focused"]} onSave={(v) => save({ geoFocus: (v || "National").toLowerCase() })} />
          {c?.geoFocus === "focused" && (
            <div className="mt-1.5">
              <BadgeMultiSelect selected={c.primaryGeographies ?? []} options={GEO_OPTIONS} placeholder="Pick regions" addLabel="Add regions" onSave={(v) => saveList({ primaryGeographies: v })} />
            </div>
          )}
          {c?.geoFocus !== "focused" && c?.primaryGeographies?.length ? (
            <div className="mt-1.5 flex flex-wrap gap-1">{c.primaryGeographies.map((g) => <Badge key={g} variant="secondary" className="text-xs">{g}</Badge>)}</div>
          ) : null}
        </div>
      </div>
      <div className="mt-3 grid gap-3 border-t pt-3 sm:grid-cols-2">
        <div>
          <Label>Sector focus</Label>
          <BadgeMultiSelect
            selected={c?.targetIndustries ?? []}
            options={INDUSTRY_OPTIONS}
            placeholder="Opportunistic (any sector)"
            onSave={(v) => saveList({ targetIndustries: v, industryFocus: v.length ? "focused" : "opportunistic" })}
          />
        </div>
        <div>
          <Label>Investment themes</Label>
          <BadgeMultiSelect selected={c?.investmentThemes ?? []} options={THEME_OPTIONS} placeholder="No specific themes" onSave={(v) => saveList({ investmentThemes: v })} />
        </div>
      </div>
    </RecordPanel>
  );
}
